import React, { useState } from "react";

/**
 * Modal di create/edit generico guidato da una config di campi — evita di
 * duplicare un form quasi identico per ogni sezione CRUD di configurazione
 * normativa (la vecchia pagina ne aveva 6 copie).
 *
 * fields: [{ key, label, type: "text"|"number"|"select"|"checkbox",
 *            options?: [{value,label}], required?, placeholder? }]
 */
export default function CrudModal({
  title,
  fields,
  initialValues = {},
  onSubmit,
  onClose,
}) {
  const [values, setValues] = useState(initialValues);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const setField = (key, value) => setValues((v) => ({ ...v, [key]: value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onSubmit(values);
      onClose();
    } catch (err) {
      setError(err.message || "Errore di salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Chiudi"
          >
            ✕
          </button>
        </div>
        <form onSubmit={submit} className="p-4 space-y-3">
          {fields.map((f) => (
            <label key={f.key} className="block text-sm">
              <span className="block text-xs text-gray-500 mb-1">
                {f.label}
                {f.required && " *"}
              </span>
              {f.type === "select" ? (
                <select
                  value={values[f.key] ?? ""}
                  onChange={(e) => setField(f.key, e.target.value)}
                  required={f.required}
                  className="border border-gray-300 rounded px-2 py-1.5 w-full"
                >
                  <option value="">—</option>
                  {(f.options || []).map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              ) : f.type === "checkbox" ? (
                <input
                  type="checkbox"
                  checked={!!values[f.key]}
                  onChange={(e) => setField(f.key, e.target.checked)}
                  className="ml-1"
                />
              ) : (
                <input
                  type={f.type === "number" ? "text" : (f.type ?? "text")}
                  inputMode={f.type === "number" ? "decimal" : undefined}
                  value={values[f.key] ?? ""}
                  onChange={(e) => setField(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  required={f.required}
                  disabled={f.disabled}
                  className="border border-gray-300 rounded px-2 py-1.5 w-full disabled:bg-gray-100"
                />
              )}
            </label>
          ))}
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="text-sm px-3 py-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={saving}
              className="bg-primary-600 text-white text-sm px-4 py-1.5 rounded hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? "Salvataggio…" : "Salva"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
